// 성장 저장(dungeonhero.growth.v2, 2026-10-21 곡선 개정): 판마다 결과 ID로 한 번만 지급되는 EXP 영수증과 스킬 트리 랭크.
// v2가 없고 v1이 정상이면 레벨을 보존해 한 번 이관한다(SKILL-TREE 6절). v1 원문은 건드리지 않는다(롤백 호환).
// 2026-10-30 720h 곡선(docs/PROGRESSION-720H.md 2절): Lv55부터 필요 EXP가 커진다. 두 곡선이 같은 구간(합계 ≤ CURVE_SPLIT_XP)은 그대로 두고,
// 그 위인데 curve-v3 영수증이 없는 원장은 이전 곡선 EXP로 보고 레벨·구간 진행률을 보존하는 보정 영수증을 한 번 더한다.
// 이 빌드가 원장을 처음 그 선 위로 올리는 쓰기는 0 EXP curve-v3 표시를 함께 쓴다. 형식·키·버전은 그대로라 이전 빌드도 읽는다.
// 정본: docs/SKILL-TREE.md 6절. 보상(save.v1)·장부(profile.v2)와 키를 나눠 기존 저장은 건드리지 않는다.
// 쓰기는 읽기-합치기-쓰기-되읽기를 한 번의 동기 호출로 한다(배우기 명령이 바로 답해야 하므로 Web Lock 대신).
import {
  FREE_RANK, LEVEL_MAX, NODE_COUNT, SKILL_NODES, XP_AT_LEVEL, XP_MAX, XP_MAX_V1, GROWTH_TEXT, GROWTH_RESET_TEXT, migrateXpV1, migrateXpV2, CURVE_SPLIT_XP,
  adjustedXp, freshRanks, learnDenial, levelFromXp, nodeIndex, pointsAtLevel, ranksValid, spentPoints,
  type GrowthCode,
} from "../shared/growth";
import type { GrowthMode, GrowthResultView, GrowthView, GameCommand } from "../shared/types";
import { rewardId, type SaveStorage } from "./save";
import type { SkillHeroId } from "../shared/skills/types";
import { ID_HASH_LEN, idHash48 } from "../shared/idHash";

/**
 * 판 EXP 영수증. deferred는 잠금 중 얻은 [raw, 몹 레벨, 잠정 보정값] 반복(제출 때 실제 레벨로 다시 보정).
 * t는 처음 제출할 때 정한 기록 시각(재시도에도 그대로 — 그 사이 접혔는지 판정, D10 원장 접기)
 */
interface RunReceipt { id: string; xp: number; deferred?: number[]; t?: number }

/** 이전 곡선(1~30)의 저장. 읽기 전용: v2 이관 원본으로만 쓴다 */
export const GROWTH_V1_KEY = "dungeonhero.growth.v1";
export const GROWTH_KEY = "dungeonhero.growth.v2";
export const GROWTH_VERSION = 2;
/** v1 → v2 레벨 보존 보정 영수증 id(한 번만, 합집합에서 중복 없음) */
export const CURVE_V2_GRANT = "curve-v2";
/**
 * 720h 곡선 이관 영수증 id(docs/PROGRESSION-720H.md 2절). 이 영수증이 있으면 원장의 EXP는 지금 곡선 기준이다.
 * 합계가 CURVE_SPLIT_XP를 넘는데 이 영수증이 없으면 이전 곡선(XP_TO_NEXT_V2)으로 쌓인 원장이라 보정 EXP를 담아 더한다.
 */
export const CURVE_V3_GRANT = "curve-v3";
export const GROWTH_BACKUP_KEY = GROWTH_KEY + ".bak";
export const GROWTH_LATEST_BACKUP_KEY = GROWTH_BACKUP_KEY + ".latest";
/** 마지막으로 성공한 정상 쓰기의 사본. 주 저장이 손상되면 이것으로 복구한다(save.v1과 같은 방식) */
export const GROWTH_GOOD_KEY = GROWTH_KEY + ".good";
/** 이전 빌드(v1)가 확인된 원장마다 남긴 정상 사본. v1 주 저장이 손상이면 이것을 이관한다 */
export const GROWTH_V1_GOOD_KEY = GROWTH_V1_KEY + ".good";
/**
 * 라이라 EXP·레벨 장부(c022, 영웅별 레벨). growth.v2와 같은 형식(version 2 원장·결과 ID 멱등 영수증)이고 키만 다르다.
 * 카엘 키(growth.v1·v2)를 읽지도 쓰지도 않으므로 옛 빌드로 롤백해도 카엘 진행이 바뀌지 않는다. v1 이관 원본이 없다(새 영웅)
 */
export const GROWTH_LYRA_KEY = "dungeonhero.growth.lyra.v1";
/** 성장 장부 저장 키 묶음(영웅별) */
interface GrowthKeys { readonly main: string; readonly good: string; readonly bak: string; readonly latest: string; readonly v1: string | null; readonly v1Good: string | null }
const KAEL_KEYS: GrowthKeys = { main: GROWTH_KEY, good: GROWTH_GOOD_KEY, bak: GROWTH_BACKUP_KEY, latest: GROWTH_LATEST_BACKUP_KEY, v1: GROWTH_V1_KEY, v1Good: GROWTH_V1_GOOD_KEY };
const LYRA_KEYS: GrowthKeys = { main: GROWTH_LYRA_KEY, good: GROWTH_LYRA_KEY + ".good", bak: GROWTH_LYRA_KEY + ".bak", latest: GROWTH_LYRA_KEY + ".bak.latest", v1: null, v1Good: null };
/** 저장 실패 뒤 다시 시도하는 간격(영웅 틱, 2초) */
const RETRY_TICKS = 120;
/** 중복 요청 ID를 기억하는 개수(최근 것만). 버튼 연타·재전송을 막는 데는 이 정도면 충분하다 */
const SEEN_LIMIT = 64;
/** 쓰기 후 되읽기 확인을 다시 시도하는 최대 횟수(다른 탭이 계속 덮어쓰는 극단 상황에서도 끝나게 한다) */
const WRITE_ATTEMPTS = 5;
export type GrowthCommand = Extract<GameCommand, { type: "learnSkill" | "resetSkills" }>;
/** commit의 build: 저장소 최신 상태에서 쓸 원장을 만든다. null = 쓸 것이 없다 */
type Build = (base: Ledger) => Ledger | null;
/** commit의 verify: 되읽은 원장(back)에 우리가 쓴 것(want)이 들어 있는지 */
type Verify = (back: Ledger, want: Ledger) => boolean;
/** 영수증이 모두 저장소에 있는지(랭크는 상관없다). 저장소 접기에 이미 들어간 영수증도 있는 것으로 본다(D10) */
const grantsStored: Verify = (back, want) => {
  const ids = new Set(back.grants.map((g) => g.id));
  return want.grants.every((g) => ids.has(g.id) || folded(back.fold, g));
};
/** 랭크 쓰기가 저장소에 그대로 남았는지(rev와 랭크 배열이 같다) */
const ranksStored: Verify = (back, want) => back.rev === want.rev && back.ranks.every((v, i) => v === want.ranks[i]);

/**
 * 판 한 번의 EXP 영수증. id는 결과 ID(클리어)나 판 고유 ID(패배·마을 복귀).
 * t(선택, D10): 이 빌드가 기록한 시각(ms). 접기는 t가 경계보다 이른 영수증을 합계로 접는다. 이전 빌드가 쓴 영수증에는 없다(옛 영수증 = 해시로 접는다)
 */
export interface Grant { id: string; xp: number; t?: number }
/**
 * 원장 접기(D10, docs/PROGRESSION-720H.md 10절): 오래된 영수증을 합계 하나로 접어 저장 크기를 묶는다. 멱등은 그대로다 —
 * t < before 인 영수증과 legacy(t 없는 옛 영수증 id의 48비트 해시, 8자씩 정렬해 이어 붙임)에 든 영수증은 이미 xp에 들어 있어 다시 더하지 않는다.
 */
export interface Fold {
  xp: number; n: number; before: number; legacy: string;
  /**
   * 최근 접은 영수증 id의 48비트 해시(접은 순서, 마지막 FOLD_RECENT_CAP개, 8자씩 이어 붙임). 같은 결과 id가 시각 없이 다시 제출될 때
   * (새로고침 뒤 결과 화면 재전송 등 — 새 영수증은 원래 t를 모른다) 한 번 지급을 지킨다(PR #291 Codex P1). 이전 형식에는 없다(빈 값)
   */
  recent: string;
}
/** recent에 남기는 접은 영수증 수(원장당 1KB). 같은 결과의 재전송은 몇 분 안에 일어나므로 몇 시간 분량이면 충분하다 */
export const FOLD_RECENT_CAP = 128;
/** 저장 원장: 영수증 집합 + 랭크(rev가 큰 쪽, 같으면 by가 큰 쪽이 이긴다) + 접은 합계(있으면 저장 version 3) */
export interface Ledger { grants: Grant[]; ranks: Uint8Array; rev: number; by: string; fold?: Fold }
type Mutable<T> = { -readonly [K in keyof T]: T[K] };
type Parsed = { ok: true; ledger: Ledger } | { ok: false; reason: "corrupt" | "future" };
type ReadResult =
  | { kind: "none" }
  | { kind: "ok"; ledger: Ledger; raw: string }
  | { kind: "migrated"; ledger: Ledger }
  | { kind: "corrupt"; raw: string }
  | { kind: "future" }
  | { kind: "unavailable" };

const record = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const integer = (v: unknown, max = Number.MAX_SAFE_INTEGER): v is number => typeof v === "number" && Number.isSafeInteger(v) && v >= 0 && v <= max;
const compare = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
const byId = (a: Grant, b: Grant): number => compare(a.id, b.id);

// ── 원장 접기(D10) ──────────────────────────────────

/** 접은 원장의 저장 version. 접기가 없으면 version 2 그대로라 이전 빌드도 읽는다. 접은 원장은 이전 빌드에 '미래'라 쓰지 않는다(접은 EXP를 지우지 않게) */
export const GROWTH_FOLD_VERSION = 3;
/** 영수증이 이 수를 넘으면 쓰기 때 접는다(약 5KB). 720h 끝 상태에서 원장 6개가 합쳐 30KB 안팎이 되게(D10 실측: 400이면 138KB) */
export const FOLD_AT = 64;
/**
 * 이 시간보다 오래된 영수증만 접는다: 같은 저장소에 10분 넘게 있던 영수증이라 접을 때 반드시 원장에 있다(다른 탭의 늦은 쓰기와 겹치지 않게).
 * 기기 사이에는 원장을 합치지 않고(계정 동기화는 키 전체 교체) 같은 기기 탭끼리만 합치므로 같은 시계를 쓴다.
 * 10분 넘게 확인하지 못한 재시도가 그 사이 접혔으면 기록된 것으로 본다(중복 대신 잃는 쪽으로 닫는다)
 */
export const FOLD_AGE_MS = 600_000;
/** legacy 해시 한 개 길이(base64url 8자 = 48비트). 옛 영수증 수천 개에서도 우연히 겹칠 확률은 무시할 만하다 */
export const LEGACY_HASH_LEN = ID_HASH_LEN;
/** 접지 않는 고정 영수증(조회하는 코드가 있다) */
const KEEP_IDS: ReadonlySet<string> = new Set([CURVE_V2_GRANT, CURVE_V3_GRANT]);
const LEGACY_RE = /^[A-Za-z0-9_-]*$/;

/** 영수증 id → 48비트 해시(base64url 8자, shared/idHash) */
export function legacyHash(id: string): string { return idHash48(id); }
const legacySets = new WeakMap<Fold, Set<string>>();
const recentSets = new WeakMap<Fold, Set<string>>();
function recentSet(f: Fold): Set<string> {
  let set = recentSets.get(f);
  if (!set) {
    set = new Set();
    for (let i = 0; i < f.recent.length; i += LEGACY_HASH_LEN) set.add(f.recent.slice(i, i + LEGACY_HASH_LEN));
    recentSets.set(f, set);
  }
  return set;
}
function legacySet(f: Fold): Set<string> {
  let set = legacySets.get(f);
  if (!set) {
    set = new Set();
    for (let i = 0; i < f.legacy.length; i += LEGACY_HASH_LEN) set.add(f.legacy.slice(i, i + LEGACY_HASH_LEN));
    legacySets.set(f, set);
  }
  return set;
}
/**
 * 영수증이 접기에 이미 들어갔는지. t가 경계보다 이르거나, id 해시가 legacy에 있으면 들어갔다.
 * 둘 다 본다: 이전 빌드 탭이 t를 지운 채 다시 쓴 영수증(legacy로 접힘)을 t가 남은 다른 탭 메모리가 되살리지 못하게
 */
export function folded(f: Fold | undefined, g: { id: string; t?: number }): boolean {
  if (!f || KEEP_IDS.has(g.id)) return false;
  if (g.t !== undefined && g.t < f.before) return true;
  if (f.legacy.length === 0 && f.recent.length === 0) return false;
  const h = legacyHash(g.id);
  return legacySet(f).has(h) || recentSet(f).has(h);
}
/** 두 접기 중 더 많이 접은 쪽(같은 원장의 이전·이후 접기 = 이후가 이전을 포함한다) */
function widerFold(a: Fold | undefined, b: Fold | undefined): Fold | undefined {
  if (!a) return b;
  if (!b) return a;
  if (a.before !== b.before) return a.before > b.before ? a : b;
  if (a.n !== b.n) return a.n > b.n ? a : b;
  return a.legacy.length >= b.legacy.length ? a : b;
}
/** 원장의 전체 EXP(남은 영수증 + 접은 합계) */
export function ledgerXp(l: Ledger): number {
  return sumXp(l.grants) + (l.fold?.xp ?? 0);
}
/**
 * 영수증이 FOLD_AT개를 넘으면 now − FOLD_AGE_MS보다 이른 영수증(t 없는 옛 영수증 포함, 고정 영수증 제외)을 접는다.
 * 같은 입력이면 같은 결과다. 접을 것이 없으면 같은 객체를 돌려준다
 */
export function foldLedger(l: Ledger, now: number): Ledger {
  if (l.grants.length <= FOLD_AT) return l;
  const prev = l.fold;
  const before = Math.max(prev?.before ?? 0, now - FOLD_AGE_MS);
  let xp = 0, n = 0;
  const keep: Grant[] = [], hashes: string[] = [], timed: Grant[] = [];
  for (const g of l.grants) {
    if (KEEP_IDS.has(g.id) || (g.t !== undefined && g.t >= before)) { keep.push(g); continue; }
    xp += g.xp; n++;
    if (g.t === undefined) hashes.push(legacyHash(g.id));
    else timed.push(g);
  }
  if (n === 0) return l;
  // 시각 있는 영수증은 오래된 것부터 recent 끝에 붙이고 마지막 FOLD_RECENT_CAP개만 남긴다(같은 시각이면 id 순 — 결정적)
  timed.sort((a, b) => (a.t! - b.t!) || compare(a.id, b.id));
  let recent = (prev?.recent ?? "") + timed.map((g) => legacyHash(g.id)).join("");
  const maxLen = FOLD_RECENT_CAP * LEGACY_HASH_LEN;
  if (recent.length > maxLen) recent = recent.slice(recent.length - maxLen);
  let legacy = prev?.legacy ?? "";
  if (hashes.length > 0) {
    const all = new Set(prev ? legacySet(prev) : []);
    for (const h of hashes) all.add(h);
    legacy = [...all].sort().join("");
  }
  return { grants: keep, ranks: new Uint8Array(l.ranks), rev: l.rev, by: l.by, fold: { xp: (prev?.xp ?? 0) + xp, n: (prev?.n ?? 0) + n, before, legacy, recent } };
}
/** 원장을 새로 만들 때 접기를 이어 붙이는 조각 */
function keepFold(l: Ledger): { fold?: Fold } {
  return l.fold ? { fold: l.fold } : {};
}
function parseFold(v: unknown): Fold | null {
  if (!record(v) || !integer(v.xp) || !integer(v.n) || !integer(v.before) || typeof v.legacy !== "string") return null;
  if (v.legacy.length % LEGACY_HASH_LEN !== 0 || !LEGACY_RE.test(v.legacy)) return null;
  const recent = v.recent === undefined ? "" : v.recent;
  if (typeof recent !== "string" || recent.length % LEGACY_HASH_LEN !== 0 || recent.length > FOLD_RECENT_CAP * LEGACY_HASH_LEN || !LEGACY_RE.test(recent)) return null;
  return { xp: v.xp, n: v.n, before: v.before, legacy: v.legacy, recent };
}

export function freshLedger(): Ledger {
  return { grants: [], ranks: freshRanks(new Uint8Array(NODE_COUNT)), rev: 0, by: "" };
}
/** 원장이 지금 곡선(720h) 기준인지(curve-v3 영수증이 있는지) */
export function onCurveV3(l: Ledger): boolean {
  return l.grants.some((g) => g.id === CURVE_V3_GRANT);
}
/**
 * 이전 곡선 원장 → 720h 곡선 원장(레벨·구간 진행률 보존). 합계 X를 migrateXpV2(X)로 만드는 보정 영수증 하나를 더한다.
 * 표시가 이미 있거나 합계가 두 곡선이 같은 구간(≤ CURVE_SPLIT_XP)이면 같은 객체를 그대로 돌려준다(멱등, 바꿀 것 없음).
 * 같은 원장에서 만든 결과는 항상 같아 두 탭이 함께 이관해도 합집합이 같다.
 */
export function migrateLedgerCurveV3(l: Ledger): Ledger {
  if (onCurveV3(l)) return l;
  const total = ledgerXp(l);
  if (total <= CURVE_SPLIT_XP) return l;
  const grants = [...l.grants, { id: CURVE_V3_GRANT, xp: Math.max(0, Math.min(XP_MAX, migrateXpV2(total) - total)) }].sort(byId);
  return { grants, ranks: new Uint8Array(l.ranks), rev: l.rev, by: l.by, ...(l.fold ? { fold: l.fold } : {}) };
}
export function sumXp(grants: readonly Grant[]): number {
  let s = 0;
  for (const g of grants) s += g.xp;
  return s;
}

/**
 * 원문 → 원장. 노드 키는 이름으로 찾고(모르는 키는 무시), 빠진 노드는 무료 랭크로 채운다.
 * version 2 원장은 접기 없음, version 3(GROWTH_FOLD_VERSION)은 접은 원장(fold 필수). 접기에 이미 든 영수증은 버린다(중복 합산 방지)
 */
export function parseLedger(raw: string, version: number = GROWTH_VERSION): Parsed {
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { ok: false, reason: "corrupt" }; }
  if (!record(parsed)) return { ok: false, reason: "corrupt" };
  const top = version === GROWTH_VERSION ? GROWTH_FOLD_VERSION : version;
  if (typeof parsed.version === "number" && parsed.version > top) return { ok: false, reason: "future" };
  if (parsed.version !== version && !(version === GROWTH_VERSION && parsed.version === GROWTH_FOLD_VERSION)) return { ok: false, reason: "corrupt" };
  let fold: Fold | undefined;
  if (parsed.version === GROWTH_FOLD_VERSION) {
    const f = parseFold(parsed.fold);
    if (!f) return { ok: false, reason: "corrupt" };
    fold = f;
  } else if (parsed.fold !== undefined) return { ok: false, reason: "corrupt" };
  // 영수증 한 건 상한: v1은 이전 곡선 최대치, v2는 새 곡선 최대치
  const grantMax = version === 1 ? XP_MAX_V1 : XP_MAX;
  if (!Array.isArray(parsed.grants) || !record(parsed.ranks) || !integer(parsed.rev) || typeof parsed.by !== "string") return { ok: false, reason: "corrupt" };
  const seen = new Map<string, Grant>();
  for (const g of parsed.grants) {
    if (!record(g) || typeof g.id !== "string" || g.id.length === 0 || g.id.length > 128 || !integer(g.xp, grantMax)) return { ok: false, reason: "corrupt" };
    if (g.t !== undefined && !integer(g.t)) return { ok: false, reason: "corrupt" };
    const grant: Grant = g.t === undefined ? { id: g.id, xp: g.xp } : { id: g.id, xp: g.xp, t: g.t };
    // 같은 ID의 영수증은 한 번만 센다(중복 지급 방지). 접기에 든 영수증도 다시 세지 않는다
    if (!seen.has(g.id) && !folded(fold, grant)) seen.set(g.id, grant);
  }
  const ranks = new Uint8Array(NODE_COUNT);
  for (let i = 0; i < NODE_COUNT; i++) ranks[i] = FREE_RANK[i];
  for (const [key, value] of Object.entries(parsed.ranks)) {
    const i = nodeIndex(key);
    if (i < 0) continue;
    if (!integer(value, 255)) return { ok: false, reason: "corrupt" };
    ranks[i] = value;
  }
  const grants = [...seen.values()].sort(byId);
  return { ok: true, ledger: { grants, ranks, rev: parsed.rev, by: parsed.by, ...(fold ? { fold } : {}) } };
}

export function serializeLedger(l: Ledger): string {
  const ranks: Record<string, number> = {};
  for (let i = 0; i < NODE_COUNT; i++) ranks[SKILL_NODES[i].id] = l.ranks[i];
  if (l.fold) return JSON.stringify({ version: GROWTH_FOLD_VERSION, grants: l.grants, ranks, rev: l.rev, by: l.by, fold: l.fold });
  return JSON.stringify({ version: GROWTH_VERSION, grants: l.grants, ranks, rev: l.rev, by: l.by });
}

/**
 * v1 원장 → v2 원장(레벨 보존). 합계가 정확히 새 곡선의 같은 위치가 되게 만든다.
 * v1 영수증 id는 남기되(같은 결과 ID 재지급 방지) EXP는 0으로 두고, 목표 EXP 전부를 보정 영수증 CURVE_V2_GRANT 한 건에 담는다.
 * 그래서 Lv30 뒤에도 쌓인 v1 EXP(합계 > XP_MAX_V1)도 Lv30으로 정확히 옮겨진다(교차 리뷰 P1). 같은 v1에서 만든 결과는 항상 같아
 * 두 탭이 함께 이관해도 합집합이 같다. 랭크·rev·by는 그대로.
 */
export function migrateLedgerV1(v1: Ledger): Ledger {
  const target = Math.max(0, Math.min(XP_MAX, migrateXpV1(sumXp(v1.grants))));
  const grants: Grant[] = v1.grants.filter((g) => g.id !== CURVE_V2_GRANT).map((g) => ({ id: g.id, xp: 0 }));
  if (target > 0) grants.push({ id: CURVE_V2_GRANT, xp: target });
  grants.sort(byId);
  return { grants, ranks: new Uint8Array(v1.ranks), rev: v1.rev, by: v1.by };
}

/**
 * 쓰기 직전: 이 빌드가 원장을 처음 두 곡선 갈림선(CURVE_SPLIT_XP) 위로 올리면 0 EXP curve-v3 표시를 함께 쓴다.
 * 쓰기 바탕(base)은 읽을 때 이미 이관됐으므로(갈림선 위 + 표시 없음 = 이관 대상) 여기까지 표시 없이 선을 넘은 EXP는 지금 곡선으로 번 것이다
 */
function stampCurveV3<T extends Ledger | null>(l: T): T {
  if (!l || onCurveV3(l) || ledgerXp(l) <= CURVE_SPLIT_XP) return l;
  return { ...l, grants: [...l.grants, { id: CURVE_V3_GRANT, xp: 0 }].sort(byId) };
}

/**
 * 다른 탭의 원장과 합친다. 영수증은 id 합집합, 랭크는 rev가 큰 쪽(같으면 by가 큰 쪽).
 * 접기는 더 많이 접은 쪽을 쓰고, 그 접기에 이미 든 영수증은 어느 쪽에서 왔든 버린다(접기 전 원장을 들고 있는 탭이 되살리지 못하게, D10)
 */
export function mergeLedgers(a: Ledger, b: Ledger): Ledger {
  const fold = widerFold(a.fold, b.fold);
  const grants = new Map<string, Grant>();
  for (const g of a.grants) if (!folded(fold, g)) grants.set(g.id, g);
  for (const g of b.grants) if (!grants.has(g.id) && !folded(fold, g)) grants.set(g.id, g);
  // curve-v3 보정은 이관한 이전 곡선 EXP가 많을수록 크다(migrateXpV2(x) − x는 x에 대해 줄지 않는다). 같은 id라도 큰 쪽이 더 많은 원장에서 나온 값이라
  // 큰 쪽을 남긴다 — 오래된 .good에서 새로 계산한 작은 보정이 이미 이관된 최신 보정을 덮으면 복구 뒤 레벨이 내려간다(Codex P1 #283)
  const ga = a.grants.find((g) => g.id === CURVE_V3_GRANT), gb = b.grants.find((g) => g.id === CURVE_V3_GRANT);
  if (ga && gb && gb.xp > ga.xp) grants.set(CURVE_V3_GRANT, gb);
  const aWins = a.rev > b.rev || (a.rev === b.rev && compare(a.by, b.by) >= 0);
  const win = aWins ? a : b;
  return { grants: [...grants.values()].sort(byId), ranks: new Uint8Array(win.ranks), rev: win.rev, by: win.by, ...(fold ? { fold } : {}) };
}

/** 노드 인덱스가 SKILL_NODES 안의 정수인지 */
const validNode = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0 && (n as number) < NODE_COUNT;

export class GrowthStore implements GrowthView {
  mode: GrowthMode = "ok";
  revision = 0;
  level = 1;
  xp = 0;
  xpLevelStart = 0;
  xpNext = XP_AT_LEVEL[2];
  pendingXp = 0;
  points = 0;
  spent = 0;
  /** 노드별 랭크. 같은 배열을 재사용하고 값만 바꾼다 */
  readonly ranks = new Uint8Array(NODE_COUNT);
  lastSaveOk = true;
  /** 마지막 레벨업(GrowthView.lastLevelUpTick·Level). 게임이 레벨이 오를 때 채운다 */
  lastLevelUpTick = -1;
  lastLevelUpLevel = 0;
  /** 마지막 명령·알림 결과. 같은 객체를 재사용한다 */
  readonly last: Mutable<GrowthResultView> = { seq: 0, kind: "none", code: "ok", node: -1, requestId: "", message: "" };
  /**
   * 이번 판 EXP 중 아직 영수증으로 제출하지 않은 몫. 제출 전까지 계속 쌓인다. null = 없음.
   * 제출(inflight)된 뒤에는 새 판 EXP가 여기로 들어와 제출된 영수증의 xp를 바꾸지 않는다.
   */
  private run: RunReceipt | null = null;
  /**
   * 제출된 영수증: 저장을 시도했지만 아직 확인되지 않은 몫(xp는 제출 순간에 고정). 쓰기가 모호하게 끝나도(쓴 뒤 되읽기 실패)
   * 같은 ID·같은 xp로 다시 확인한다. 확인되면 비운다.
   */
  private inflight: RunReceipt | null = null;
  /** 기록 요청(commitRun)이 있었고 아직 끝나지 않았다. 남은 판 EXP도 제출 대상이다 */
  private wantsFlush = false;
  /** 저장 경로가 쓰는 원장(저장소와 합친 최신). 화면·전투 값은 아래 스냅샷에서만 읽는다 */
  private ledger: Ledger = freshLedger();
  /** 원장의 확정 EXP 합계와 쓴 포인트. 원장이 바뀔 때만 다시 계산한다(처치마다 영수증을 다시 더하지 않는다) */
  private confirmedXp = 0;
  private spentPts = 0;
  /**
   * 화면·전투가 읽는 스냅샷: 확정 EXP·쓴 포인트·랭크. 안전한 경계(생성, 마을 진입, 던전 입장, 명시적 refresh, 배우기 성공)에서만 갱신한다.
   * 판 진행 중 다른 탭이 쓴 원장을 저장 때문에 읽어도 레벨·랭크·능력치(스킬 4 해금)는 바뀌지 않는다.
   */
  private snapXp = 0;
  private snapSpent = 0;
  /** 최근 요청 ID(최대 SEEN_LIMIT). 삽입 순서로 가장 오래된 것부터 버린다 */
  private readonly seen = new Set<string>();
  private readonly nonce: string;
  private retryTicks = 0;
  /** 손상 원문을 백업한 뒤 정상 사본으로 복구한 횟수 */
  recovered = 0;
  /** v1 → v2 레벨 보존 이관을 시도한 횟수(쓰기 성공 뒤에는 v2가 있으므로 다시 일어나지 않는다) */
  migrated = 0;

  /** 이 장부의 영웅(c022). 카엘 = growth.v2(옛 키 그대로), 라이라 = growth.lyra.v1 */
  readonly hero: SkillHeroId;
  private readonly keys: GrowthKeys;

  /** 환생으로 이어 가는 습득 스킬 비트(레인 R, game/rebirth가 정한다). 0회차 장부는 0 */
  private rebirthKept = 0;
  /** 전직 스킬 비트(C10-5, game/jobRun이 전직 영수증으로 정한다 — content/jobQuest.ts jobSkillMask). 장부에 쓰지 않는다 */
  private jobBits = 0;
  /** 습득 스킬로 이어 붙이는 비트 = 환생 kept | 전직 스킬. growthMods·learnedSkills·슬롯·UI가 모두 이 값을 kept로 읽는다 */
  get kept(): number { return this.rebirthKept | this.jobBits; }
  set kept(v: number) { this.rebirthKept = v; }
  /** 전직 스킬 비트를 바꾼다(바뀔 때만 revision을 올려 슬롯·HUD가 다시 읽게 한다) */
  setJobBits(bits: number): void { if (bits !== this.jobBits) { this.jobBits = bits; this.revision++; } }

  /** key(레인 R): 환생 회차 장부 키(game/rebirth growthKeyOf). 주면 v1 이관 없이 그 키 묶음(.good·.bak·.bak.latest)을 쓴다 */
  /** 영수증 기록 시각(ms). 테스트가 바꾼다 */
  private readonly now: () => number;

  constructor(private readonly storage: SaveStorage, options: { nonce?: string; hero?: SkillHeroId; key?: string; now?: () => number } = {}) {
    this.nonce = options.nonce ?? rewardId();
    this.now = options.now ?? Date.now;
    this.hero = options.hero ?? "kael";
    const k = options.key;
    this.keys = k ? { main: k, good: k + ".good", bak: k + ".bak", latest: k + ".bak.latest", v1: null, v1Good: null } : this.hero === "kael" ? KAEL_KEYS : LYRA_KEYS;
    this.refresh();
  }

  /** 원장을 바꾸고 캐시(확정 EXP·쓴 포인트)를 함께 갱신한다. 원장을 바꾸는 곳은 모두 여기로 온다 */
  private setLedger(l: Ledger): void {
    this.ledger = l;
    this.confirmedXp = ledgerXp(l);
    this.spentPts = spentPoints(l.ranks);
  }

  // ── 읽기·합치기 ─────────────────────────────────

  private readStorage(): ReadResult {
    let raw: string | null;
    try { raw = this.storage.getItem(this.keys.main); } catch { return { kind: "unavailable" }; }
    if (raw === null) return this.readV1();
    const parsed = parseLedger(raw);
    // 720h 곡선 이관(curve-v3): 표시가 없으면 이전 곡선 원장 — 보정 영수증을 더해 한 번 쓴다(v1 이관과 같은 길)
    if (parsed.ok) {
      const m = migrateLedgerCurveV3(parsed.ledger);
      return m === parsed.ledger ? { kind: "ok", ledger: parsed.ledger, raw } : { kind: "migrated", ledger: m };
    }
    return parsed.reason === "future" ? { kind: "future" } : { kind: "corrupt", raw };
  }

  /**
   * v2가 없을 때: v1이 정상이면 이관한 원장을 돌려준다(쓰기는 syncLedger가 commit으로 한다). v1이 없거나
   * 손상·미래면 이관하지 않고 빈 시작(none)으로 둔다 — v1 원문은 그대로 남는다.
   * v1 읽기 예외는 unavailable: 빈 v2를 만들면 다음부터 v1을 읽지 않아 기존 진행도가 영구히 빠진다(교차 리뷰 P1).
   */
  private readV1(): ReadResult {
    let raw: string | null;
    const k = this.keys;
    if (k.v1 === null || k.v1Good === null) return { kind: "none" };
    try { raw = this.storage.getItem(k.v1); } catch { return { kind: "unavailable" }; }
    if (raw === null) return { kind: "none" };
    const parsed = parseLedger(raw, 1);
    if (parsed.ok) return { kind: "migrated", ledger: migrateLedgerV1(parsed.ledger) };
    // v1 주 저장이 손상이면 이전 빌드가 확인 뒤 남긴 정상 사본(v1 .good)을 이관한다(PR #145 Codex 7차).
    // 사본 읽기 예외는 unavailable(빈 v2로 시작하면 다음부터 v1을 안 읽는다), 사본도 없거나 손상이면 빈 시작
    if (parsed.reason !== "corrupt") return { kind: "none" };
    let good: string | null;
    try { good = this.storage.getItem(k.v1Good); } catch { return { kind: "unavailable" }; }
    const back = good === null ? null : parseLedger(good, 1);
    return back && back.ok ? { kind: "migrated", ledger: migrateLedgerV1(back.ledger) } : { kind: "none" };
  }

  private readGood(): Ledger | null {
    try {
      const raw = this.storage.getItem(this.keys.good);
      if (raw === null) return null;
      const parsed = parseLedger(raw);
      return parsed.ok ? migrateLedgerCurveV3(parsed.ledger) : null;
    } catch { return null; }
  }

  /** 손상 원문을 .bak(최초)·.latest(이후)에 남기고 되읽어 확인한다. 실패하면 주 저장을 건드리지 않는다 */
  private backupCorrupt(raw: string): boolean {
    try {
      const k = this.keys;
      const first = this.storage.getItem(k.bak);
      if (first === null) this.storage.setItem(k.bak, raw);
      else if (first !== raw && this.storage.getItem(k.latest) !== raw) this.storage.setItem(k.latest, raw);
      return this.storage.getItem(k.bak) === raw || this.storage.getItem(k.latest) === raw;
    } catch { return false; }
  }

  private setMode(mode: GrowthMode): void {
    if (this.mode !== mode) { this.mode = mode; this.revision++; this.project(); }
  }

  /**
   * 저장소를 다시 읽고 화면·전투 스냅샷도 맞춘다. 생성·마을 진입·던전 입장·배우기 전에만 부른다.
   * 판 진행 중에는 부르지 않는다(저장 경로는 syncLedger만 쓴다).
   */
  refresh(): void {
    this.syncLedger();
    this.takeSnapshot();
  }

  /** 스냅샷을 다시 맞출 때마다 오른다. GameSim이 보고 전투 보정(gmods·능력치)을 다시 계산한다(잠금 복구 때 포함) */
  snapshotEpoch = 0;

  /** 화면·전투 스냅샷을 원장 값으로 맞춘다(안전한 경계에서만) */
  private takeSnapshot(): void {
    this.snapshotEpoch++;
    this.snapXp = this.confirmedXp;
    this.snapSpent = this.spentPts;
    this.ranks.set(this.ledger.ranks);
    this.changed();
  }

  /**
   * 저장소를 다시 읽어 원장과 합친다(저장 경로용). 화면·전투 스냅샷은 건드리지 않는다.
   * 예외: 잠금(읽기 실패 등)에서 정상으로 돌아온 순간은 스냅샷이 잠금 중 값(빈 원장)이므로 지금 원장으로 맞춘다.
   * 저장 경로(판 기록·떠나기)에서만 불리므로 전투 중 랭크가 바뀌지 않는다(PR #145 Codex 4차).
   */
  /** 반환값: 잠금에서 풀려 스냅샷을 원장으로 다시 맞췄는지(그 스냅샷에는 이미 저장된 영수증이 들어 있다) */
  private syncLedger(): boolean {
    const wasLocked = this.mode !== "ok";
    const r = this.readStorage();
    switch (r.kind) {
      case "unavailable": this.setMode("unavailable"); break;
      case "future": this.setMode("future"); break;
      case "none": this.setMode("ok"); break;
      // 저장소가 이 탭보다 같거나 새롭다(쓰기는 항상 저장소에 먼저 간다). 동점이면 저장소가 이긴다
      case "ok": this.setLedger(mergeLedgers(r.ledger, this.ledger)); this.setMode("ok"); break;
      // v1 → v2 이관·720h 곡선 이관(curve-v3): 합친 원장을 한 번 쓴다(실패하면 다음 동기화에서 같은 이관을 다시 한다)
      case "migrated":
        this.setLedger(mergeLedgers(r.ledger, this.ledger));
        this.setMode("ok");
        this.migrated++;
        this.commit((base) => mergeLedgers(base, r.ledger), grantsStored);
        break;
      case "corrupt": this.recoverCorrupt(r.raw); break;
    }
    this.normalize();
    if (wasLocked && this.mode === "ok") { this.takeSnapshot(); return true; }
    this.changed();
    return false;
  }

  /** 손상 주 저장: 백업 확인 뒤 마지막 정상 사본이 있으면 그것으로 이어 쓰고, 없으면 corrupt 잠금 */
  private recoverCorrupt(raw: string): void {
    if (!this.backupCorrupt(raw)) { this.setMode("unavailable"); return; }
    const good = this.readGood();
    if (!good) { this.setMode("corrupt"); return; }
    this.setLedger(mergeLedgers(good, this.ledger));
    this.recovered++;
    this.setMode("ok");
    this.commit((base) => ({ grants: base.grants, ranks: new Uint8Array(base.ranks), rev: base.rev, by: base.by, ...keepFold(base) }), grantsStored);
  }

  /**
   * 랭크가 규칙을 어기면(손상·다른 빌드) 랭크만 무료 랭크로 바꾸고 EXP는 보존한다.
   * 메모리 랭크는 쓰기 성공과 관계없이 바꾼다(스킬 4·캡스톤을 쓸 수 없게). rev는 쓰기가 성공했을 때만 올린다.
   */
  private normalize(): void {
    if (this.mode !== "ok") return;
    if (ranksValid(this.ledger.ranks, levelFromXp(this.confirmedXp))) return;
    this.last.seq++;
    this.last.kind = "notice";
    this.last.code = "ranks_reset";
    this.last.node = -1;
    this.last.requestId = "";
    this.last.message = GROWTH_TEXT.ranks_reset;
    const fresh = freshRanks(new Uint8Array(NODE_COUNT));
    this.setLedger({ grants: this.ledger.grants, ranks: fresh, rev: this.ledger.rev, by: this.ledger.by, ...keepFold(this.ledger) });
    this.commit((base) => ({ grants: base.grants, ranks: new Uint8Array(fresh), rev: base.rev + 1, by: this.nonce, ...keepFold(base) }), ranksStored);
  }

  // ── 표시값 ──────────────────────────────────────

  /**
   * 표시·레벨 필드를 원장과 판 EXP에서 다시 계산한다.
   * 잠금 모드(future·corrupt·unavailable)에서는 판 EXP가 저장되지 않으므로 레벨에 반영하지 않는다(표시 레벨 = 확정 레벨).
   * 판 EXP는 pendingXp로 계속 보이고 XpGain 이벤트는 그대로 난다.
   */
  private project(): void {
    // 판 EXP(제출 전 + 제출 중)는 저장되기 전까지 대기로 보이고, 레벨에는 잠금 모드가 아닐 때만 반영한다
    const pending = (this.run?.xp ?? 0) + (this.inflight?.xp ?? 0);
    const locked = this.mode !== "ok";
    const xp = locked ? this.snapXp : this.snapXp + pending;
    const lv = levelFromXp(xp);
    this.xp = xp;
    this.level = lv;
    this.pendingXp = pending;
    this.xpLevelStart = XP_AT_LEVEL[lv];
    this.xpNext = lv >= LEVEL_MAX ? XP_MAX : XP_AT_LEVEL[lv + 1];
    this.points = pointsAtLevel(lv);
    this.spent = this.snapSpent;
  }

  private changed(): void {
    this.project();
    this.revision++;
  }

  // ── 쓰기 ────────────────────────────────────────

  /** 성공하면 원장을 바꾸고 true. 실패하면 원장을 그대로 두고 lastSaveOk = false */
  private rawWrite(l: Ledger): boolean {
    const json = serializeLedger(l);
    try { this.storage.setItem(this.keys.main, json); } catch { return false; }
    try { this.storage.setItem(this.keys.good, json); } catch { /* 사본 실패는 주 저장 성공을 바꾸지 않는다 */ }
    return true;
  }

  /**
   * 원장을 쓰고 되읽어 확인한다. build(base)는 저장소의 최신 상태(base)에서 쓸 원장을 만든다. null이면 쓸 것이 없다(이미 기록됨).
   * verify(back, want)가 참이면 성공이다. 다른 탭이 사이에 덮어썼으면(verify 거짓) 최신과 합쳐 build를 다시 적용해 쓴다(WRITE_ATTEMPTS회).
   * 끝까지 실패하면 lastSaveOk = false이고 false를 돌려준다. 호출자는 대기 상태를 유지해야 한다.
   */
  private commit(build: Build, verify: Verify): boolean {
    // 쓰기 직전에 저장소를 다시 읽어 합친다: 이 탭이 마지막으로 읽은 뒤 다른 탭이 쓴 영수증을 덮어쓰지 않게(교차 리뷰 P1, 이관 경합).
    // v2가 아직 없으면(이관 전) 이 탭의 원장 그대로. 읽기 실패·손상·미래면 기존처럼 이 탭 원장으로 시도하고 되읽기에서 걸러진다.
    const fresh = this.readStorage();
    let base = fresh.kind === "ok" || fresh.kind === "migrated" ? mergeLedgers(fresh.ledger, this.ledger) : this.ledger;
    for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt++) {
      // 쓸 때마다 오래된 영수증을 접는다(D10 저장 크기). 접기는 결과 EXP 합을 바꾸지 않는다
      const built = stampCurveV3(build(base));
      const want = built && foldLedger(built, this.now());
      if (!want) {
        // 만들 것이 없다 = 저장소 최신(base)이 이미 원하는 상태다(예: 다른 탭이 먼저 초기화). 성공으로 끝내되 이 탭 원장도 그 최신으로 맞춘다.
        // 안 맞추면 호출자(배우기·초기화)가 이어 찍는 스냅샷이 낡은 랭크를 그대로 복사한다. 저장 경로라 화면 스냅샷은 여기서 건드리지 않는다.
        this.setLedger(base);
        this.lastSaveOk = true; this.changed(); return true;
      }
      if (!this.rawWrite(want)) break;
      const back = this.readStorage();
      if (back.kind !== "ok") break;
      if (verify(back.ledger, want)) {
        // 합집합이 갈림선을 처음 넘을 수도 있다(두 탭의 영수증이 각각은 선 아래) — 다시 쓸 때도 표시를 함께 쓴다(Codex P2 #283)
        const merged = stampCurveV3(mergeLedgers(back.ledger, want));
        // 저장소에 없는 영수증을 이 탭이 알고 있으면 합쳐 다시 쓴다(best effort: 주 저장은 이미 확인됐다)
        if (serializeLedger(merged) !== serializeLedger(back.ledger)) this.rawWrite(merged);
        this.setLedger(merged);
        this.lastSaveOk = true;
        this.changed();
        return true;
      }
      base = mergeLedgers(back.ledger, this.ledger);
    }
    this.lastSaveOk = false;
    this.changed();
    return false;
  }

  // ── 판 EXP ──────────────────────────────────────

  /** 판 중 EXP 획득. 저장은 판이 끝날 때(commitRun)나 페이지를 떠날 때에만 한다 */
  /**
   * amount는 지금 표시 레벨로 레벨 차 보정을 마친 값. 잠금(저장 레벨을 모름) 중에는 raw·몹 레벨을 함께 남겨
   * 저장이 다시 읽히는 순간 실제 레벨로 다시 보정한다(잠금 중 Lv1 자리값으로 보정 없이 확정되지 않게, PR #145 Codex 6차).
   */
  addRunXp(amount: number, raw?: number, mobLevel?: number): void {
    if (!(amount > 0)) return;
    if (!this.run) this.run = { id: rewardId(), xp: 0 };
    this.run.xp += amount;
    if (this.mode !== "ok" && raw !== undefined && mobLevel !== undefined) (this.run.deferred ??= []).push(raw, mobLevel, amount);
    this.changed();
  }

  /** 잠금 중 쌓인 EXP를 지금 확정 레벨로 다시 보정한다(정상 모드에서 제출 직전 한 번) */
  private settleDeferred(r: RunReceipt): void {
    const d = r.deferred;
    if (!d || this.mode !== "ok") return;
    const level = levelFromXp(this.confirmedXp);
    for (let k = 0; k < d.length; k += 3) r.xp += adjustedXp(d[k], level, d[k + 1]) - d[k + 2];
    r.xp = Math.max(0, r.xp);
    r.deferred = undefined;
  }

  /**
   * 판 끝(클리어·패배·마을 복귀·페이지 숨김): 이번 판 EXP를 영수증으로 기록한다. resultId가 있으면(클리어) 그것을 영수증 ID로 쓴다.
   * 이미 같은 ID가 있으면 중복 지급하지 않고 대기만 끝낸다. 실패하면 대기 상태로 남아 틱마다 다시 쓴다.
   * 기록 뒤에도 판은 이어진다: 이후 EXP는 새 영수증으로 쌓이므로 합계는 한 번씩만 센다.
   */
  commitRun(resultId?: string): void {
    if (resultId && this.run) this.run.id = resultId;
    this.wantsFlush = true;
    this.flushPending();
  }

  /**
   * 기록 요청을 처리한다. 1) 이미 제출된 영수증(inflight)을 같은 ID·같은 xp로 확인한다(모호한 쓰기 뒤 재시도 포함).
   * 2) 기록 요청이 남아 있으면 판 EXP를 지금 xp로 고정해 새 영수증으로 제출한다. 제출 뒤 들어온 EXP는 새 run에 쌓여 영향을 받지 않는다.
   * 실패하면 제출된 영수증을 그대로 두고 틱마다 다시 확인한다. 성공하면 확정 EXP 스냅샷에 더한다(총 EXP는 그대로).
   */
  private flushPending(): boolean {
    if (this.inflight && !this.submit(this.inflight)) return false;
    if (this.wantsFlush && this.run) {
      if (this.run.xp <= 0) this.run = null;
      else {
        this.inflight = this.run;
        this.run = null;
        if (!this.submit(this.inflight)) return false;
      }
    }
    this.wantsFlush = false;
    this.changed();
    return true;
  }

  /** 제출된 영수증 하나를 저장소에 확인시킨다. 저장소 최신 목록에 같은 ID가 있으면(이전 시도가 기록했다면) 쓰지 않고 확인한다 */
  private submit(r: RunReceipt): boolean {
    // 잠금에서 풀리며 스냅샷을 다시 맞췄고 그 원장에 이 영수증이 이미 있으면(다른 탭이 먼저 기록) 스냅샷에 이미 들어 있다(PR #145 Codex 5차)
    const resnapped = this.syncLedger();
    const inSnapshot = resnapped && (this.ledger.grants.some((g) => g.id === r.id) || folded(this.ledger.fold, r));
    this.settleDeferred(r);
    if (this.mode !== "ok") { this.lastSaveOk = false; this.changed(); return false; }
    // 영수증은 rev를 바꾸지 않는다. ID가 저장소에 있거나 접기에 이미 들었으면 같은 xp로 기록된 것이므로 중복 지급 없이 확인만 한다.
    // 기록 시각 t는 처음 쓸 때 한 번 정하고(접기 경계보다 이르지 않게 — 기기 시계가 뒤로 가도 새 영수증이 접힌 것으로 보이지 않게) 재시도에도 그대로 쓴다
    const ok = this.commit(
      (base) => {
        if (base.grants.some((g) => g.id === r.id) || folded(base.fold, r)) return null;
        if (r.t === undefined) r.t = Math.max(this.now(), base.fold?.before ?? 0);
        return { grants: [...base.grants, { id: r.id, xp: r.xp, t: r.t }].sort(byId), ranks: new Uint8Array(base.ranks), rev: base.rev, by: base.by, ...keepFold(base) };
      },
      (back) => back.grants.some((g) => g.id === r.id) || folded(back.fold, r),
    );
    if (!ok) return false;
    this.inflight = null;
    if (!inSnapshot) this.snapXp += r.xp;
    return true;
  }

  /** 틱마다 불린다. 저장 실패로 제출된 판 EXP를 2초마다 다시 확인한다 */
  tick(): void {
    if (this.inflight && !this.lastSaveOk && ++this.retryTicks >= RETRY_TICKS) {
      this.retryTicks = 0;
      this.flushPending();
    }
  }

  // ── 배우기·초기화 ─────────────────────────────────

  /** 요청 ID를 기억한다. 최근 SEEN_LIMIT개만 남긴다 */
  private remember(id: string): void {
    this.seen.add(id);
    if (this.seen.size > SEEN_LIMIT) {
      const oldest = this.seen.values().next();
      if (!oldest.done) this.seen.delete(oldest.value);
    }
  }

  /**
   * 배우기·초기화 명령. 결과 코드를 돌려준다. 중복 요청(duplicate)은 last와 revision을 건드리지 않고 조용히 무시한다.
   * 노드 번호가 SKILL_NODES 범위의 정수가 아니면 bad_node. denied는 GameSim이 정한 문맥 거절(일시정지·마을 창 밖).
   * 이번 판 EXP가 아직 저장되지 않았으면 배우기를 막는다(저장 전 레벨 기준으로 랭크를 쓰지 않는다).
   */
  command(cmd: GrowthCommand, denied?: GrowthCode): GrowthCode {
    const node = cmd.type === "learnSkill" ? cmd.node : -1;
    let code: GrowthCode;
    if (cmd.type === "learnSkill" && !validNode(cmd.node)) code = "bad_node";
    else if (this.seen.has(cmd.requestId)) return "duplicate";
    else {
      this.remember(cmd.requestId);
      if (denied) code = denied;
      else if (this.run || this.inflight) code = "save_failed";
      else {
        this.refresh();
        if (this.mode !== "ok") code = "readonly";
        else if (cmd.type === "learnSkill") code = this.learn(node);
        else code = this.reset();
      }
    }
    const last = this.last;
    last.seq++;
    last.kind = cmd.type === "learnSkill" ? "learn" : "reset";
    last.code = code;
    last.node = node;
    last.requestId = cmd.requestId;
    last.message = code === "ok" && cmd.type === "resetSkills" ? GROWTH_RESET_TEXT : GROWTH_TEXT[code];
    this.revision++;
    return code;
  }

  /**
   * 한 랭크 배우기. 다른 탭이 사이에 쓴 경우에도 최신 랭크 위에서 거절 사유를 다시 판정하고 다시 적용한다.
   * 거절 사유는 build 안에서 정하고, 쓰기 결과(ok/save_failed)보다 우선한다.
   */
  private learn(node: number): GrowthCode {
    let denial = null as GrowthCode | null;
    const ok = this.commit(
      (base) => {
        const d = learnDenial(base.ranks, levelFromXp(ledgerXp(base)), node);
        if (d) { denial = d; return null; }
        const ranks = new Uint8Array(base.ranks);
        ranks[node]++;
        return { grants: base.grants, ranks, rev: base.rev + 1, by: this.nonce, ...keepFold(base) };
      },
      ranksStored,
    );
    if (denial) return denial;
    if (ok) this.takeSnapshot();
    return ok ? "ok" : "save_failed";
  }

  private reset(): GrowthCode {
    const fresh = freshRanks(new Uint8Array(NODE_COUNT));
    const ok = this.commit(
      (base) => (fresh.every((v, i) => v === base.ranks[i])
        ? null
        : { grants: base.grants, ranks: new Uint8Array(fresh), rev: base.rev + 1, by: this.nonce, ...keepFold(base) }),
      ranksStored,
    );
    if (ok) this.takeSnapshot();
    return ok ? "ok" : "save_failed";
  }
}
